import {
    Box,
    BoxProps,
    InternalStandardProps,
    List,
    ListItemButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Layout } from "../Layout";
import { AppTypography } from "JS/React/Components/AppTypography";
import { useNavigate } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { clearLocalStorage, isBetaEnvironment } from "JS/Helpers";
import { config } from "JS/Config";
import { useResetGlobalState } from "JS/React/Hooks/Media";
import localforage from "localforage";
import { useEffect, useMemo, useState } from "react";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import {
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
} from "JS/Models";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { AppDivider } from "JS/React/Components/AppDivider";
import {
    useDeleteDeviceToken,
    usePermissionHelpers,
} from "JS/React/Hooks/Notification";
import { messaging } from "JS/Helpers/UserMessaging";
import { getLocalFcmToken, getWebcastUUID } from "JS/Helpers/PushNotifications";
import { useTeamInformation } from "JS/React/Hooks/Home";
import { optOutEndpoint } from "JS/Services/pinPointService/pinpointEndpointService";
import { useGlobalMediaEssentials } from "JS/React/Hooks/MediaEssentials";
import { ensurePinpointKeys } from "JS/Helpers/PinpointKeys";
import { useResumeData } from "JS/React/Hooks/Firebase/Resume";
import { useAppSelector } from "JS/Redux/Store";
import { UserRoute } from "../../../Helpers/FycStatusConstants";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useJwtToken } from "JS/React/Hooks/Users";

const MoreMenuRoot = styled(Box)({});

const Grey600Text = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
}));

const StyledListItem = styled(ListItemButton)(({ theme }) => ({
    fontWeight: 400,
    display: "flex",
    justifyContent: "flex-start",
}));

const StyledList = styled(List)({
    flexGrow: 1,
});

const ListIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontWeight: 400,
    fontSize: "24px",
    marginRight: "5%",
    width: "10%",
    color: theme.palette.grey[600],
}));

const LargeListIcon = styled(ListIcon)({
    fontSize: "28px",
});

const VersionText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.common.black,
}));

export interface MoreMenuProps extends InternalStandardProps<BoxProps> {}

export const clearLocalforageKeepHistory = async () => {
    const PERSIST_KEY = "persist:REACT_APP_WEB_MEDIA";
    const raw = await localforage.getItem(PERSIST_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw as string);
    const userObj = parsed?.userHistory ? JSON.parse(parsed?.userHistory) : {};
    const uniqueIds = userObj?.login_details?.reduce((acc, curr) => {
        if (!acc.some((item) => item.member_id === curr.member_id)) {
            acc.push(curr);
        }
        return acc;
    }, []);

    const preserved = {
        userHistory: JSON.stringify({
            login_details: uniqueIds,
        }),
        _persist: parsed._persist ?? { version: -1, rehydrated: true },
    };

    await localforage.setItem(PERSIST_KEY, JSON.stringify(preserved));
};

export const MoreMenu = (props: MoreMenuProps) => {
    const { className, ...rest } = props;

    const navigate = useNavigate();
    const { linkProvider } = useRouting();
    const { resetReduxState } = useResetGlobalState();
    const { enqueueSnackbar } = useSnackbar();

    const [isOpenConfirmDailog, setIsOpenConfirmDailog] = useState(false);
    const [logoutLoader, setLogoutLoader] = useState(false);

    const { logFirebaseEvent } = useFirebaseLogger();

    const { deleteDeviceToken } = useDeleteDeviceToken();
    const { disableJwtToken } = useJwtToken();
    const { initializeNotifications } = usePermissionHelpers();
    const { mediaEssentials } = useGlobalMediaEssentials();

    const {
        logAudioResume,
        logVideoResume,
        logPlaylistResume,
        logAudioBundleResume,
    } = useResumeData();

    const { refetch, loading: loadingTeamInfo } = useTeamInformation(true);

    const userTeamInformation = useAppSelector(
        (state) => state.home.userTeamInformation,
    );

    useEffect(() => {
        refetch();
    }, []);

    const isApprenticeSubcriber = useMemo(
        () =>
            !!userTeamInformation?.activeSubscriptions?.includes("Apprentice"),
        [userTeamInformation],
    );

    const logout = async () => {
        const disableJwt = async () => {
            try {
                await disableJwtToken();
            } catch (err) {
                enqueueSnackbar(messaging.logout.failed, {
                    variant: "error",
                });
                initializeNotifications();
                throw err;
            }
        };

        const token = getLocalFcmToken();
        const deleteToken = async () => {
            try {
                await deleteDeviceToken(config.user.memberId, token);
                if (mediaEssentials?.enable_pinpoint) {
                    await ensurePinpointKeys();
                    const deviceId = getWebcastUUID();
                    await optOutEndpoint(config.user.memberId, deviceId);
                }
            } catch (err) {
                enqueueSnackbar(messaging.logout.failed, {
                    variant: "error",
                });
                throw err;
            }
        };
        try {
            setLogoutLoader(true);
            if (!!token) await deleteToken();
            await disableJwt();

            try {
                await logAudioResume();
                await logVideoResume();
                await logPlaylistResume();
                await logAudioBundleResume();
            } catch (error) {
                throw error;
            }
            resetReduxState();
            clearLocalStorage();
            await clearLocalforageKeepHistory();

            const action: FirebaseEventAction = {
                action: EventActions.LOGOUT,
                category: EventCategories.GENERAL,
            };

            logFirebaseEvent(EventNames.LOGGED_OUT, action);
            setTimeout(() => setLogoutLoader(false), 1000);
            window.location.reload();
        } catch (err) {
            setLogoutLoader(false);
        }
    };
    const handleToggleConfirmDialog = () => {
        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const downloadQueue = localStorage.getItem("downloadQueue")
        ? JSON.parse(localStorage.getItem("downloadQueue"))
        : [];

    const handleNavigateToQueue = () =>
        navigate(linkProvider.react.downloadingQueue().index());

    return (
        <Layout>
            <MoreMenuRoot
                {...rest}
                className={className}
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
            >
                <StyledList>
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.gifts().index())
                        }
                    >
                        <ListIcon icon={["fas", "gift"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Gifts
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(
                                linkProvider.react.giftDashboard().index(),
                            )
                        }
                    >
                        <ListIcon icon={["fas", "dashboard"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Gift Dashboard
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.documents().index())
                        }
                    >
                        <ListIcon icon={["far", "clipboard"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Documents
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.favorites().index())
                        }
                    >
                        <ListIcon icon={["fas", "star"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Favorites
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.events().index())
                        }
                    >
                        <ListIcon icon={["far", "address-card"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            My Event Registrations
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    {isApprenticeSubcriber && (
                        <>
                            <StyledListItem
                                onClick={() =>
                                    navigate(
                                        linkProvider.react
                                            .fycStatus()
                                            .index(UserRoute.PRIMARY_USER),
                                    )
                                }
                            >
                                <LargeListIcon icon={["far", "id-badge"]} />
                                <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                                    My First Year Status
                                </Grey600Text>
                            </StyledListItem>
                            <AppDivider />
                        </>
                    )}
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.playlist().index())
                        }
                    >
                        <ListIcon icon={["fas", "music"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Audio Playlists
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.webcast().index())
                        }
                    >
                        <ListIcon icon={["fas", "earth-americas"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Webcast
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.appInfo().index())
                        }
                    >
                        <ListIcon icon={["far", "file-lines"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            App Info
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={handleNavigateToQueue}
                    >
                        <ListIcon icon={["fas", "file-arrow-down"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Downloading Queue
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(linkProvider.react.feedback().index())
                        }
                    >
                        <ListIcon icon={["fas", "message"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Feedback
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={() =>
                            navigate(
                                linkProvider.react.appSettings().index(),
                            )
                        }
                    >
                        <ListIcon icon={["fas", "cogs"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Settings
                        </Grey600Text>
                    </StyledListItem>
                    <AppDivider />
                    <StyledListItem
                        onClick={handleToggleConfirmDialog}
                    >
                        <ListIcon icon={["fas", "power-off"]} />
                        <Grey600Text variant="h6" sx={{ fontWeight: 400 }}>
                            Logout
                        </Grey600Text>
                    </StyledListItem>

                    <AppDivider />
                </StyledList>
                <VersionText textAlign={"right"} variant="caption">
                    Version:{" "}
                    {isBetaEnvironment()
                        ? config.betaAppVersion
                        : config.appVersion}
                </VersionText>
                {isOpenConfirmDailog && (
                    <AppConfimationDialog
                        open={isOpenConfirmDailog}
                        onConfirm={logout}
                        btnConfirmLoader={logoutLoader}
                        onClose={handleToggleConfirmDialog}
                        popupMainText={
                            downloadQueue?.length > 0
                                ? `Downloading is in-progress in Queue. Downloading Queue data will be lost. Are you sure you want to log out?`
                                : "Are you sure you want to logout?"
                        }
                        confirmText="Logout"
                        cancelButtonText="Cancel"
                        showConfirmFirst={true}
                    />
                )}
                {loadingTeamInfo && (
                    <AppBackdropProgress
                        open={loadingTeamInfo}
                        backdropText={messaging.loader.loadingSession}
                    />
                )}
            </MoreMenuRoot>
        </Layout>
    );
};
